import { JSX, useState, useEffect } from "react";
import "./App.css";
import SideBar from "./empreenderUI/SideBar";
import ItemMenu from "./empreenderUI/ui/ItemMenu";
import ItemIcon from "./empreenderUI/ui/ItemIcon";
import ItemText from "./empreenderUI/ui/ItemText";
import ItemSub from "./empreenderUI/ItemSub";
import Separator from "./empreenderUI/ui/Separator";
import { useTranslation } from "react-i18next";
import { appPadrao, appsDaCentral, SLUG_PLUS } from "./lib/appsDaCentral";
import { Route, Switch, useLocation } from "wouter";
import ContentApp from "./empreenderUI/ContentApp";
import TelaInicial from "./pages/TelaInicial";
import TelaChat from "./pages/TelaChat";
import { EmpreenderApps } from "./files/AppsArray";
import { usePergunta } from "./contexts/PerguntaContext";
import { useHistorico } from "./contexts/HistoricoContext";
import CampoBusca from "./components/CampoBusca";
import SeletorIdioma from "./components/SeletorIdioma";
import FecharMenuNoCelular, { pedirFecharMenuNoCelular } from "./components/FecharMenuNoCelular";
import ItemConversa from "./components/ItemConversa";
import { excluirConversa } from "./lib/armazenamento";
import Admin from "./pages/admin/Admin";
import AdminMetricas from "./pages/admin/AdminMetricas";
import DefinirSenha from "./pages/admin/DefinirSenha";
import CentralEmbutida from "./pages/CentralEmbutida";
import TesteIframe from "./pages/TesteIframe";
import { PREFIXO_EMBUTIDO } from "./lib/rotas";
import Login from "./pages/admin/Login";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import i18n, { supportedLanguages } from "./i18n";
import VerificaSeUsuarioEstaNoMobile from "./hooks/VerificaSeUsuarioEstaNoMobile";
const { v4: uuidv4 } = require("uuid");

interface Aplicativo {
  name: string;
  namees: string;
  eplus: string;
  icon: JSX.Element;
  tags: string[];
  link: string;
  content: string;
  slug: string;
  sluges: string;
  slugamigavel: string;
}

const ProtectedRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { user } = useAuth();
  const [location, setLocation] = useLocation();

  if (!user && location.startsWith("/admin")) {
    setLocation("/admin/login");
    return null;
  }

  return children;
};

const ROTA_TESTE_IFRAME = "/teste-iframe";

function App() {
  const [location, setLocation] = useLocation();
  const [busca, setBusca] = useState("");
  const { appName, setAppName, appSlug, setAppSlug, setConversaAtual } =
    usePergunta();
  const { historicoApps, recarregarHistorico } = useHistorico();
  const { t } = useTranslation();
  const idiomaAtual = i18n.language;
  const isMobile = VerificaSeUsuarioEstaNoMobile();

  useEffect(() => {
    // Verifica se é uma rota de administração
    const isAdminRoute = location.startsWith("/admin");
    if (isAdminRoute) return;
    // O modo embutido trata app e idioma por conta própria (pages/CentralEmbutida.tsx).
    if (location.startsWith(PREFIXO_EMBUTIDO) || location === ROTA_TESTE_IFRAME) return;

    // Obtém o parâmetro "app" da URL
    const urlParams = new URLSearchParams(window.location.search);
    const appEplusUrl = urlParams.get("app");
    const appIdioma = urlParams.get("lang");

    // Se o parâmetro "app" for encontrado, ajusta o estado
    if (appEplusUrl) {
      const dadosApp = EmpreenderApps.find((app) => app.eplus === appEplusUrl);
      console.log("dadosApp:", dadosApp);

      if (dadosApp) {
        // Define o nome e o slug do aplicativo conforme o idioma
        setAppName(idiomaAtual === "es" ? dadosApp.namees : dadosApp.name);
        setAppSlug(idiomaAtual === "es" ? dadosApp.sluges : dadosApp.slug);

        if (appIdioma !== idiomaAtual)
          i18n.changeLanguage(appIdioma || idiomaAtual);

        // Forma a nova URL para redirecionamento
        const novaUrl = `/${dadosApp.slugamigavel}/${idiomaAtual}`;
        console.log("novaUrl:", novaUrl);

        // Verifica se a URL atual já é a mesma que a nova URL para evitar loop
        if (location !== novaUrl) {
          // Redireciona para a nova URL, se for diferente da atual
          setLocation(novaUrl);
        }
      }
    } else {
      // Verifica se a URL possui o idioma (pt ou es)
      // Com a barra: sem ela, "/365artes" terminava em "es" e virava espanhol.
      const temIdioma = location.match(
        new RegExp(`/(${supportedLanguages.join("|")})$`)
      );

      // Verifica se a URL corresponde a uma rota de tela inicial ou chat
      const rotaTelaInicialOuChat =
        location.match(/^\/(?:[a-z0-9-]+\/)?[a-z0-9-]+\/?[a-z0-9-]*$/) !== null;

      // Sem idioma na URL, usa o detectado (país do IP / navegador) em vez de forçar "pt".
      if (!temIdioma && rotaTelaInicialOuChat) {
        setLocation(`${location.toString().replace(/\/$/, "")}/${idiomaAtual}`);
      } else if (!temIdioma && location === "/") {
        setLocation(`/${idiomaAtual}`);
      } else if (temIdioma) {
        // Se houver idioma na URL, altera o idioma da aplicação
        const idioma = temIdioma[1];
        if (idioma !== i18n.language) i18n.changeLanguage(idioma);
      }
    }
  }, [location, idiomaAtual, setAppName, setAppSlug]);

  // Plus fixo no topo, depois os outros apps em ordem alfabética (filtrados pela busca do menu).
  const aplicativosFiltrados = appsDaCentral(idiomaAtual, busca);

  const mudaAppAtivo = (aplicativo: Aplicativo) => {
    setAppName(idiomaAtual === "es" ? aplicativo.namees : aplicativo.name);
    setAppSlug(idiomaAtual === "es" ? aplicativo.sluges : aplicativo.slug);
    setConversaAtual(uuidv4);

    const novaUrl =
      location === "/"
        ? `/${idiomaAtual}`
        : `/${aplicativo.slugamigavel}/${idiomaAtual}`;

    setLocation(novaUrl);

    // No celular o menu cobre a tela: escolheu o app, fecha. App com conversas anteriores (a seta)
    // fica aberto, porque o clique abre a lista delas para a pessoa escolher.
    const temConversas =
      Object.keys(historicoApps?.[aplicativo.slugamigavel] || {}).length > 0;
    if (!temConversas) pedirFecharMenuNoCelular();
  };

  const abreConversa = (
    link: string,
    conversa: string,
    appname: string,
    appslug: string
  ) => {
    setAppName(appname);
    setAppSlug(appslug);
    setConversaAtual(conversa);

    const novaUrl = link + "/" + idiomaAtual;

    setLocation(novaUrl);
    pedirFecharMenuNoCelular();
  };

  // Excluir pelo menu: se a conversa está aberta, volta para a tela inicial do app.
  const apagaConversa = (slugAmigavel: string, conversa: string) => {
    excluirConversa(slugAmigavel, conversa);
    recarregarHistorico();
    if (location.startsWith(`/${slugAmigavel}/chat/${conversa}`)) {
      setConversaAtual(uuidv4());
      setLocation(`/${slugAmigavel}/${idiomaAtual}`);
    }
  };

  const isAuthRoute = location.startsWith("/admin");

  if (location.startsWith(PREFIXO_EMBUTIDO)) return <CentralEmbutida />;
  // Painel de mentira para testar o iframe do "Ajuda" (só no npm start).
  if (process.env.NODE_ENV === "development" && location === ROTA_TESTE_IFRAME) return <TesteIframe />;

  return (
    <AuthProvider>
      <div>
        {isAuthRoute ? (
          <Switch>
            {/* Métricas: senha própria (conferida na api/metricas), fora do login da SAK. */}
            <Route path="/admin/metricas">
              <AdminMetricas />
            </Route>
            {/* Link de convite: a pessoa cria a própria senha (token depois do "#"). */}
            <Route path="/admin/convite">
              <DefinirSenha />
            </Route>
            <Route path="/admin/login">
              <Login />
            </Route>
            <Route path="/admin">
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            </Route>
            <Route path="/admin/conversas">
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            </Route>
          </Switch>
        ) : (
          <SideBar
            app="Suporte Empreender"
            appEndPoint="app.empreender.plus"
            isOpen={!isMobile}
            hideApps
            hideDash
            idioma = {idiomaAtual === "es" ? "es-mx" : "pt-br"}
          >
            <CampoBusca
              placeholder="Buscar aplicativo"
              aoBuscar={(valor) => setBusca(valor)}
            />
            {aplicativosFiltrados.flatMap((aplicativo, indice) => {
              const isActive =
                (idiomaAtual === "es" ? aplicativo.namees : aplicativo.name) ===
                appName;
              const item = (
                <ItemMenu
                  key={aplicativo.eplus}
                  isActive={isActive}
                  onClick={() => mudaAppAtivo(aplicativo)}
                  className="itemsLimite"
                >
                  <div className="icone-sup">
                    <ItemIcon>{aplicativo.iconwhite}</ItemIcon>
                  </div>
                  <ItemText>
                    {idiomaAtual === "es" ? aplicativo.namees : aplicativo.name}
                  </ItemText>

                  {historicoApps &&
                    historicoApps[aplicativo.slugamigavel] &&
                    Object.entries(historicoApps[aplicativo.slugamigavel]).map(
                      ([conversaId, mensagens]) => (
                        <ItemSub
                          key={conversaId}
                          onClick={() =>
                            abreConversa(
                              `/${aplicativo.slugamigavel}/chat/${conversaId}`,
                              conversaId,
                              aplicativo.name,
                              aplicativo.slug
                            )
                          }
                        >
                          <ItemConversa
                            titulo={(() => {
                              const palavras = mensagens[0].texto.split(" ");
                              return (
                                palavras
                                  .slice(0, 4)
                                  .join(" ")
                                  .toLowerCase()
                                  .replace(/^\w/, (letra) => letra.toUpperCase()) +
                                (palavras.length > 4 ? "..." : "")
                              );
                            })()}
                            aoExcluir={() => apagaConversa(aplicativo.slugamigavel, conversaId)}
                          />
                        </ItemSub>
                      )
                    )}
                </ItemMenu>
              );
              // Separador entre o Plus (a porta de entrada) e os demais apps.
              const plusComApps =
                aplicativo.slugamigavel === SLUG_PLUS && indice < aplicativosFiltrados.length - 1;
              return plusComApps
                ? [item, <Separator key="separador-apps">{t("todos_os_apps")}</Separator>]
                : [item];
            })}
            <ContentApp>
              {/* Filho direto da SideBar não aparece (ela só desenha os tipos que conhece);
                  dentro do ContentApp aparece, e o portal leva o seletor para a barra do topo. */}
              <SeletorIdioma />
              <FecharMenuNoCelular />
              <Route path="/:appSlugAmigavel/chat/:conversaAtual/:lang">
                {(params) => {
                  const dadosApp = EmpreenderApps.find(
                    (app) => app.slugamigavel === params.appSlugAmigavel
                  );
                  if (dadosApp) {
                    setAppName(
                      idiomaAtual === "es" ? dadosApp.namees : dadosApp.name
                    );
                    setAppSlug(
                      idiomaAtual === "es" ? dadosApp.sluges : dadosApp.slug
                    );
                  }
                  return (
                    <TelaChat
                      appSlugAmigavel={params.appSlugAmigavel}
                      conversaAtual={params.conversaAtual}
                    />
                  );
                }}
              </Route>
              <Route path="/:appSlug/:lang">
                {(params) => {
                  const dadosApp = EmpreenderApps.find(
                    (app) => app.slugamigavel === params.appSlug
                  );
                  if (dadosApp) {
                    setAppName(
                      idiomaAtual === "es" ? dadosApp.namees : dadosApp.name
                    );
                    setAppSlug(
                      idiomaAtual === "es" ? dadosApp.sluges : dadosApp.slug
                    );
                  }
                  return <TelaInicial appSlug={appSlug} appName={appName} />;
                }}
              </Route>
              <Route path="/:lang">
                {(params) => {
                  const dadosApp = appPadrao;
                  if (params.lang === "es") {
                    setAppName(dadosApp?.namees || "");
                    setAppSlug(dadosApp?.sluges || "");
                  } else {
                    setAppName(dadosApp?.name || "");
                    setAppSlug(dadosApp?.slug || "");
                  }
                  return <TelaInicial appSlug={appSlug} appName={appName} />;
                }}
              </Route>
              <Route path="/">
                {(params) => {
                  const dadosApp = appPadrao;
                  setAppName(dadosApp?.name || "");
                  setAppSlug(dadosApp?.slug || "");
                  return (
                    <TelaInicial
                      appSlug={dadosApp?.slug || ""}
                      appName={dadosApp?.name || ""}
                    />
                  );
                }}
              </Route>
            </ContentApp>
          </SideBar>
        )}
      </div>
    </AuthProvider>
  );
}

export default App;
